import { html } from '@resn/gozer-core';
import { styleMap } from 'lit-html/directives/style-map.js';

import { Component, componentMixin } from '@resn/gozer-core/component';
import DomComponentMixin from '@resn/gozer-core-mixins/dom-component';
import SectionMixin from 'component/sections/base/section-mixin';
import SectionFallbackMixin from 'component/sections/base/section-fallback-mixin';
import { lerp, modulo } from '@resn/gozer-math';

import { deviceBenchmark } from 'utils/device-benchmark';
import { COLORS } from 'core/constants';
import getStore from 'core/store';
import { isTablet } from '@resn/gozer-env';

const store = getStore();

import './index.scss';
import ResizeService from '@resn/gozer-services/resize-service';
import { autobind } from 'core-decorators';
import { debounce } from 'underscore';

const template = ({ options }) => html`
    <section
        class="section  scrollingText ${options.className} ${options.inverseColors ? 'inverseColors' : ''}"
        ?padding=${options.padding}
    >
        ${options.lines.map(
            (line) => html`
                <h2
                    data-ui="line"
                    class="js-line  scrollingText__text  text-heading1"
                    style=${styleMap({
                        color: line.color || 'white',
                    })}
                ></h2>
            `,
        )}
    </section>
`;

@componentMixin(DomComponentMixin)
@componentMixin(deviceBenchmark.fallback ? SectionFallbackMixin : SectionMixin)
class ScrollingText extends Component {
    props = {};

    options = {
        template,
        padding: true,
        inverseColors: false,
        bgColor: COLORS.BLUE,
        className: '',
        lines: [
            { text: 'line 1 line 1 line 1', textColor: 'white' },
            { text: 'line 2 line 2 line 2', textColor: COLORS.LIGHT_BLUE },
        ],
    };

    xText = 0;
    xTextAmbient = 0;

    async onSetup() {
        this.el.style.backgroundColor = this.options.bgColor;

        this.linesEl = [...this.el.querySelectorAll('.js-line')];
        this.lines = await Promise.all(
            this.options.lines.map(async (_, i) => {
                return await new ScrollingLineItem().setup({
                    options: {
                        appendTo: this.linesEl[i],
                        numItems: 3,
                        textLine: _.text,
                        dir: i % 2 ? -1 : 1,
                    },
                    props: {},
                });
            }),
        );

        this.onResize = debounce(this.onResize, 200);
        ResizeService.add(this.onResize, true);
    }

    update() {
        if (isTablet) return;

        const { mobile, scale, scrollDir } = store.state;
        const { scrollProgress } = this.props;

        const xdist = (mobile ? 120 : 300) * scale;
        const xText = lerp(scrollProgress, -xdist, xdist);

        this.xTextAmbient += (mobile ? 1.25 : 2.5) * scrollDir;
        this.xText = xText + this.xTextAmbient;

        for (let i = 0; i < this.lines.length; i++) {
            const line = this.lines[i];
            line.props.pX = this.xText;
        }
    }

    @autobind
    onResize() {
        for (let i = 0; i < this.lines.length; i++) {
            const line = this.lines[i];
            line.resize();
        }
    }

    onDestroy() {
        ResizeService.remove(this.resize);
    }

    calculateBounds() {}
}

export default ScrollingText;

@componentMixin(DomComponentMixin)
class ScrollingLineItem extends Component {
    options = {
        template: ({ options }) => {
            // const txt = options.textLine;
            const tpls = [];
            const tpl = html`<span> ${options.textLine} </span>`;
            for (let i = 0; i < options.numItems; i++) tpls.push(tpl);
            return html`<span>${tpls}<span class="ref">${options.textLine}</span></span>`;
        },
        dir: 1,
    };

    props = {
        pX: 0,
        widthTotal: 0,
    };

    async onSetup() {
        this.items = [];
        for (let i = 0; i < this.el.children.length; i++) {
            const el = this.el.children[i];
            if (!el.classList.contains('ref'))
                this.items.push({
                    el,
                    xBase: 0,
                    width: 0,
                });
        }
    }

    setPX() {
        // update
        const { pX, widthTotal } = this.props;
        const { dir } = this.options;
        for (let i = 0; i < this.items.length; i++) {
            const it = this.items[i];

            let x = it.xBase + pX * dir;
            x = modulo(x, widthTotal);

            it.el.style.transform = `translate3d(${x}px, 0, 0.1px)`;
        }
    }

    @autobind
    resize() {
        let widthTotal = 0;
        for (let i = 0; i < this.items.length; i++) {
            const it = this.items[i];
            const br = it.el.getBoundingClientRect();
            it.width = br.width;
            it.xBase = widthTotal;

            widthTotal += it.width;
        }
        this.el.style.left = `${-widthTotal * 0.5}px`;
        this.props.widthTotal = widthTotal;
    }

    onDestroy() {
        this.items = null;
    }
}

export { ScrollingLineItem };
